import LoaderOverlay from '@allesauto/core/loaderOverlay';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { isWinterLausitzShop } from '@allesauto/utils/funcs.ts';
import { SCREEN_BREAKPOINT, useScreenBreakpoint } from '@allesauto/utils/hooks';
import clsx from 'clsx';
import React, { FC, ReactNode, useRef } from 'react';

import WlFooterWinterLausitzNav from '@/components/Footer/WlFooterWinterLausitzNav';

import { featureflags } from '@/global/featureflags/featureflags';
import { useStickySidebar } from '@/global/hooks/useStickySidebar';
export interface PCPLayoutProps {
  suppressHydrationWarning?: boolean;
  headerContent: ReactNode;
  horizontalToolbar?: ReactNode;
  sidebarContent: ReactNode;
  seoContent?: ReactNode;
  footerContent: ReactNode;
  mobileFilter: ReactNode;
  isVisible: boolean;
  isLoading?: boolean;
  isPCPLoading?: boolean;
}

const PCPLayout: FC<React.PropsWithChildren<PCPLayoutProps>> = ({
  headerContent,
  footerContent,
  children,
  sidebarContent,
  seoContent,
  horizontalToolbar,
  mobileFilter,
  isVisible,
  isLoading = false,
  isPCPLoading = false,
}) => {
  const whitelabel = useWhiteLabel();
  const asideRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const seoContentRef = useRef<HTMLDivElement>(null);
  const breakpoint = useScreenBreakpoint();
  const largeDevice = breakpoint >= SCREEN_BREAKPOINT.LG;
  const sideBarUnStickyRef = seoContent ? seoContentRef : footerRef;

  const asideCssClass = clsx(
    'aa-sidebar bg-purple80 border-grey10 fixed h-full max-lg:z-40 mb-0 overflow-y-auto sidebar-top-class text-white lg:block lg:w-[25vw]',
    whitelabel ? 'monochrome:bg-brandColor' : 'monochrome:bg-grey80',
    isVisible ? 'block z-50 w-full' : 'hidden'
  );

  useStickySidebar(sideBarUnStickyRef.current, asideRef.current);

  return (
    <>
      <div
        suppressHydrationWarning
        className="flex flex-1 flex-col overflow-hidden w-full"
      >
        {headerContent}
        {mobileFilter}
        <div
          className={clsx(
            'bg-grey10 flex min-h-[calc(100vh-10rem)] relative',
            whitelabel && 'lg:z-0'
          )}
        >
          <aside
            data-sidebar1="sidebar"
            data-tracking-id="aa-sidebar"
            ref={asideRef}
            className={asideCssClass}
          >
            <div className="mb-10rem pb-16px space-y-6">{sidebarContent}</div>
          </aside>
          <main
            data-main="main"
            data-tracking-id="aa-main"
            className="bg-grey10 flex-1 overflow-y-auto pb-100px lg:ml-[calc(25vw)] xl:ml-[calc(25vw)]"
          >
            <div className="px-20px lg:pl-[2rem] xl:pl-[2rem] monochrome:bg-white">
              {horizontalToolbar}
            </div>
            <div className="px-20px lg:pl-[2rem] xl:pl-[2rem]">{children}</div>
          </main>
          {isLoading && <LoaderOverlay />}
          {isPCPLoading && <LoaderOverlay hasLoader={false} hasZeroOpacity />}
        </div>
        {seoContent && (
          <div
            ref={seoContentRef}
            data-tracking-id="aa-seo-content"
            className="bg-white"
          >
            {seoContent}
          </div>
        )}
        {((!isVisible && !largeDevice) || largeDevice) &&
          (isWinterLausitzShop(whitelabel) ? (
            <>
              <WlFooterWinterLausitzNav />
            </>
          ) : (
            <>
              <footer
                data-tracking-id="aa-footer"
                ref={footerRef}
                className="bg-grey90 pb-0 text-white"
              >
                {footerContent}
              </footer>
            </>
          ))}
      </div>
    </>
  );
};

export default PCPLayout;
